import React, { useEffect, useState } from 'react';
import axios from 'axios';
import CurrencyForm from './CurrencyForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

function CurrencyTable() {
  const { canCreate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [editCurrency, setEditCurrency] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('currency_code');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(FALLBACK_PAGE_SIZE);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);

  useDefaultPageSize(setLimit);

  useEffect(() => {
    fetchCurrencies();
    // eslint-disable-next-line
  }, [search, sortBy, sortOrder, page, limit, status]);

  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const params = {
        search,
        sortBy,
        sortOrder,
        page,
        limit,
        status,
      };
      const res = await axios.get('/api/currencies', { params });
      console.log('API Response:', res.data);
      setCurrencies(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      console.error('Error fetching currencies:', err);
      setCurrencies([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(col);
      setSortOrder('ASC');
    }
  };

  // Add currency
  const handleAdd = () => {
    setEditCurrency(null);
    setShowForm(true);
  };

  // Edit currency
  const handleEdit = (currency) => {
    setEditCurrency(currency);
    setShowForm(true);
  };

  // Deactivate currency
  const handleDeactivate = async (currency) => {
    if (!(await askConfirm(`Deactivate ${currency.currency_code} - ${currency.currency_name}?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/currencies/${currency.id}/deactivate`);
      fetchCurrencies();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  // Reactivate currency
  const handleReactivate = async (currency) => {
    try {
      await axios.patch(`/api/currencies/${currency.id}/reactivate`);
      fetchCurrencies();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  // Save currency (add or edit)
  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editCurrency) {
        await axios.put(`/api/currencies/${editCurrency.id}`, data);
      } else {
        await axios.post('/api/currencies', data);
      }
      setShowForm(false);
      fetchCurrencies();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Currency Codes</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
            >
              Add Currency
            </button>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          placeholder="Search currency..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: 160 }}
        />
        <select className="styled-input" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>
      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <CurrencyForm
            key={editCurrency?.id ?? 'add'}
            initial={editCurrency}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}
      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '22%' }} />
            <col style={{ width: '60%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '6%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('currency_code')}>Code {sortBy === 'currency_code' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('currency_name')}>Currency Name {sortBy === 'currency_name' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>In Use</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : currencies.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : currencies.map((currency) => (
              <tr
                key={currency.id}
                className="list-row"
                onClick={() => { if (!showForm) handleEdit(currency); }}
                style={{ cursor: showForm ? 'default' : 'pointer' }}
                title={showForm ? undefined : 'Click to edit'}
              >
                <td style={{ padding: '6px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14, fontWeight: 600 }}>{currency.currency_code}</td>
                <td style={{ padding: '6px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14 }}>{currency.currency_name}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 14 }}>—</td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(currency) },
                      status === 'active'
                        ? { key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(currency) }
                        : { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(currency) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Currencies"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default CurrencyTable;
